import type { ReelItem } from '~/components/PropertyReelsFeed/types';
import type { Property } from '~/core/entities/property';

import { useFeedState, useVideoHistory } from '~/composables/shorts';
import { propertyImages } from '~/composables/shorts/mappers';
import { shortVideoToReelItem } from '~/composables/shorts/useVideoHistory';
import { useShortsFeedStore } from '~/store/shortsFeedStore';
import { useVideoHistoryStore } from '~/store/videoHistoryStore';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { storeToRefs } from 'pinia';
import { toRaw } from 'vue';

function mapPropertyToReelItem(property: Property): ReelItem | null {
	const featuredVideo = property.featuredVideos?.[0];
	if (featuredVideo) {
		return {
			videoUrl: featuredVideo.url,
			thumbnailUrl: featuredVideo.thumbnail ?? null,
			property: toRaw(property),
			videoType: 'native',
			videoWidth: featuredVideo.width,
			videoHeight: featuredVideo.height
		};
	}

	const images = propertyImages(property);

	if (!images.length) return null;

	return {
		videoUrl: null,
		thumbnailUrl: images[0] ?? null,
		images,
		property: toRaw(property),
		videoType: 'native'
	};
}

export function useShortsFeedOrchestrator() {
	const feedStore = useShortsFeedStore();
	const historyStore = useVideoHistoryStore();

	const { loadHistory, loadMoreHistory, resetHistory } = useVideoHistory();
	const {
		loadMore: loadMoreFeed,
		reset: resetFeed,
		seedFromCarousel
	} = useFeedState();

	const {
		videos,
		hasMore,
		isLoading: isFeedLoading,
		recommendationCountVideos,
		recommendationCountPhotos
	} = storeToRefs(feedStore);
	const { historyItems, hasMoreHistory, isLoadingHistory } =
		storeToRefs(historyStore);

	const targetItem = ref<ReelItem | null>(null);
	const isTargetLoading = ref(false);

	const feedItems = computed(() => videos.value.map(shortVideoToReelItem));
	const targetCommercialId = computed(
		() => targetItem.value?.property.commercialId
	);
	const visibleHistoryItems = computed(() => {
		const seenCommercialIds = new Set<string>();
		const targetId = targetCommercialId.value;
		const items: ReelItem[] = [];

		for (const item of historyItems.value) {
			const commercialId = item.property.commercialId;
			if (commercialId === targetId) continue;
			if (seenCommercialIds.has(commercialId)) continue;

			seenCommercialIds.add(commercialId);
			items.push(item);
		}

		return items;
	});
	const historyCount = computed(() => visibleHistoryItems.value.length);

	const reelItems = computed(() => {
		const seenCommercialIds = new Set<string>();
		const merged: ReelItem[] = [];

		for (const item of visibleHistoryItems.value) {
			const commercialId = item.property.commercialId;
			seenCommercialIds.add(commercialId);
			merged.push(item);
		}

		if (targetItem.value) {
			const commercialId = targetItem.value.property.commercialId;
			seenCommercialIds.add(commercialId);
			merged.push(targetItem.value);
		}

		for (const item of feedItems.value) {
			merged.push(item);
		}

		return merged;
	});

	const isLoading = computed(
		() => isTargetLoading.value || isFeedLoading.value || isLoadingHistory.value
	);

	async function loadTargetItem(commercialId: string): Promise<void> {
		const userSession = useUserSession();
		const getCookieData = useCookieData();

		isTargetLoading.value = true;
		try {
			const [property, error] = await nuxtPropertiesRepository.getOne(
				commercialId,
				userSession.user.value?.id,
				getCookieData()
			);

			if (error || !property) {
				throw createError({
					statusCode: 404,
					statusMessage: 'Imóvel não encontrado'
				});
			}

			const mappedItem = mapPropertyToReelItem(property);
			targetItem.value = mappedItem;
		} finally {
			isTargetLoading.value = false;
		}
	}

	function setTargetFromProperty(property: Property): void {
		const mappedItem = mapPropertyToReelItem(property);
		if (!mappedItem) {
			throw createError({
				statusCode: 404,
				statusMessage: 'Imóvel sem short disponível'
			});
		}
		targetItem.value = mappedItem;
	}

	function resetAll(): void {
		targetItem.value = null;
		resetHistory();
		resetFeed();
	}

	function resetHistoryAndFeed(): void {
		resetHistory();
		resetFeed();
	}

	return {
		reelItems,
		historyCount,
		hasMore,
		hasMoreHistory,
		isLoading,
		recommendationCountVideos,
		recommendationCountPhotos,
		loadHistory,
		loadMoreHistory,
		loadMoreFeed,
		loadTargetItem,
		setTargetFromProperty,
		resetAll,
		resetHistoryAndFeed,
		seedFromCarousel
	};
}
